import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Pill, Brain, AlertTriangle, Send, Mic, FileText, Package,
  ArrowLeft, Sparkles, Shield, CheckCircle, AlertCircle, Loader2
} from "lucide-react";
import { Link } from "react-router-dom";
import VoiceInput, { VoiceInputIcon } from "@/components/VoiceInput";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIEPrescriptionDashboard() {
  const [activeTab, setActiveTab] = useState("recommend");
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [voicePrescription, setVoicePrescription] = useState(null);
  const [formulary, setFormulary] = useState(null);
  
  // Patient profile form
  const [patientProfile, setPatientProfile] = useState({
    patientId: "",
    patientName: "",
    age: "",
    diagnosis: "",
    prognosisDays: "",
    allergies: "",
    currentMeds: "",
    renalFunction: "normal",
    hepaticFunction: "normal"
  });
  
  // Symptom form
  const [symptomForm, setSymptomForm] = useState({
    symptom: "pain",
    severity: "moderate",
    currentTreatmentIneffective: false,
    additionalContext: ""
  });

  useEffect(() => {
    loadFormulary();
  }, []);

  const loadFormulary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai-rx/formulary`);
      const data = await res.json();
      setFormulary(data);
    } catch (err) {
      console.error("Failed to load formulary:", err);
    }
  };

  const getRecommendation = async () => {
    if (!patientProfile.patientId || !patientProfile.patientName) {
      toast.error("Please enter patient ID and name");
      return;
    }

    setLoading(true);
    try {
      const allergiesArray = patientProfile.allergies
        ? patientProfile.allergies.split(",").map(a => a.trim())
        : [];
      
      const medsArray = patientProfile.currentMeds
        ? patientProfile.currentMeds.split("\n").filter(m => m.trim()).map(m => {
            const parts = m.split(" ");
            return { name: parts[0], dose: parts.slice(1).join(" ") };
          })
        : [];

      const res = await fetch(`${API_URL}/api/ai-rx/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_profile: {
            patient_id: patientProfile.patientId,
            patient_name: patientProfile.patientName,
            age: patientProfile.age ? parseInt(patientProfile.age) : null,
            diagnosis: patientProfile.diagnosis || null,
            prognosis_days: patientProfile.prognosisDays ? parseInt(patientProfile.prognosisDays) : null,
            allergies: allergiesArray,
            current_medications: medsArray,
            renal_function: patientProfile.renalFunction,
            hepatic_function: patientProfile.hepaticFunction
          },
          symptom: symptomForm.symptom,
          severity: symptomForm.severity,
          current_treatment_ineffective: symptomForm.currentTreatmentIneffective,
          additional_context: symptomForm.additionalContext || null
        })
      });
      
      const data = await res.json();
      setRecommendation(data);
      toast.success("AI recommendation generated");
    } catch (err) {
      toast.error("Failed to get recommendation");
    }
    setLoading(false);
  };

  const handleVoiceTranscription = async (transcription) => {
    if (!patientProfile.patientId || !patientProfile.patientName) {
      toast.error("Please enter patient ID and name first");
      return;
    }

    setLoading(true);
    try {
      // Convert audio to base64 and send to voice-to-prescription
      const res = await fetch(`${API_URL}/api/ai-rx/voice-to-prescription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientProfile.patientId,
          patient_name: patientProfile.patientName,
          audio_base64: transcription.audioBase64,
          prescriber_npi: "1234567890",
          prescriber_name: "Dr. Provider"
        })
      });
      
      const data = await res.json();
      setVoicePrescription(data);
      toast.success("Voice prescription parsed");
    } catch (err) {
      // Fallback: use the transcription text directly
      setVoicePrescription({
        transcription: transcription.text,
        parsed_prescription: null,
        ready_to_send: false
      });
      toast.info("Voice transcribed - manual review needed");
    }
    setLoading(false);
  };

  const sendToPharmacy = async (prescription) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-rx/send-to-pharmacy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientProfile.patientId,
          patient_name: patientProfile.patientName,
          medication: prescription.name,
          strength: prescription.strength,
          form: prescription.form,
          quantity: prescription.quantity,
          days_supply: prescription.days_supply,
          sig: prescription.sig,
          refills: prescription.refills || 0,
          prescriber_npi: "1234567890",
          prescriber_name: "Dr. Provider",
          pharmacy_ncpdp: "1234567",
          is_controlled: prescription.schedule !== "non-controlled",
          schedule: prescription.schedule === "non-controlled" ? null : prescription.schedule
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(`Prescription sent! Confirmation: ${data.pharmacy_confirmation}`);
      }
    } catch (err) {
      toast.error("Failed to send prescription");
    }
    setLoading(false);
  };

  const severityColors = {
    high: "bg-red-100 text-red-800 border-red-200",
    moderate: "bg-yellow-100 text-yellow-800 border-yellow-200",
    low: "bg-green-100 text-green-800 border-green-200"
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6" data-testid="ai-eprescription-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/integrations" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">AI E-Prescription</h1>
              <p className="text-slate-600">Intelligent medication workflow with AI assistance</p>
            </div>
          </div>
          <Badge className="bg-violet-100 text-violet-700 border-violet-200">
            <Sparkles className="w-3 h-3 mr-1" />
            AI-Powered
          </Badge>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="recommend" className="flex items-center gap-2" data-testid="recommend-tab">
              <Brain className="w-4 h-4" />
              AI Recommend
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex items-center gap-2" data-testid="voice-tab">
              <Mic className="w-4 h-4" />
              Voice Prescribe
            </TabsTrigger>
            <TabsTrigger value="comfort-kit" className="flex items-center gap-2" data-testid="comfort-kit-tab">
              <Package className="w-4 h-4" />
              Comfort Kits
            </TabsTrigger>
          </TabsList>

          {/* AI Recommendation Tab */}
          <TabsContent value="recommend" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Patient Profile Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Patient Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Patient ID *</Label>
                      <Input
                        value={patientProfile.patientId}
                        onChange={(e) => setPatientProfile({...patientProfile, patientId: e.target.value})}
                        placeholder="P12345"
                        data-testid="rx-patient-id"
                      />
                    </div>
                    <div>
                      <Label>Patient Name *</Label>
                      <Input
                        value={patientProfile.patientName}
                        onChange={(e) => setPatientProfile({...patientProfile, patientName: e.target.value})}
                        placeholder="John Smith"
                        data-testid="rx-patient-name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Age</Label>
                      <Input
                        type="number"
                        value={patientProfile.age}
                        onChange={(e) => setPatientProfile({...patientProfile, age: e.target.value})}
                        placeholder="75"
                      />
                    </div>
                    <div>
                      <Label>Prognosis (days)</Label>
                      <Input
                        type="number"
                        value={patientProfile.prognosisDays}
                        onChange={(e) => setPatientProfile({...patientProfile, prognosisDays: e.target.value})}
                        placeholder="30"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Primary Diagnosis</Label>
                    <Input
                      value={patientProfile.diagnosis}
                      onChange={(e) => setPatientProfile({...patientProfile, diagnosis: e.target.value})}
                      placeholder="Metastatic lung cancer"
                    />
                  </div>
                  
                  <div>
                    <Label>Allergies (comma-separated)</Label>
                    <Input
                      value={patientProfile.allergies}
                      onChange={(e) => setPatientProfile({...patientProfile, allergies: e.target.value})}
                      placeholder="Penicillin, Sulfa"
                    />
                  </div>
                  
                  <div>
                    <Label>Current Medications (one per line)</Label>
                    <Textarea
                      value={patientProfile.currentMeds}
                      onChange={(e) => setPatientProfile({...patientProfile, currentMeds: e.target.value})}
                      placeholder="Morphine 30mg q4h&#10;Ondansetron 4mg PRN&#10;Lorazepam 0.5mg PRN"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Renal Function</Label>
                      <Select value={patientProfile.renalFunction} onValueChange={(v) => setPatientProfile({...patientProfile, renalFunction: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="mild_impairment">Mild Impairment</SelectItem>
                          <SelectItem value="moderate_impairment">Moderate Impairment</SelectItem>
                          <SelectItem value="severe_impairment">Severe Impairment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Hepatic Function</Label>
                      <Select value={patientProfile.hepaticFunction} onValueChange={(v) => setPatientProfile({...patientProfile, hepaticFunction: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="mild_impairment">Mild Impairment</SelectItem>
                          <SelectItem value="moderate_impairment">Moderate Impairment</SelectItem>
                          <SelectItem value="severe_impairment">Severe Impairment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Symptom & Request Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="w-5 h-5 text-violet-600" />
                    Symptom to Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Symptom *</Label>
                    <Select value={symptomForm.symptom} onValueChange={(v) => setSymptomForm({...symptomForm, symptom: v})}>
                      <SelectTrigger data-testid="symptom-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pain">Pain</SelectItem>
                        <SelectItem value="dyspnea">Dyspnea (Shortness of Breath)</SelectItem>
                        <SelectItem value="nausea">Nausea/Vomiting</SelectItem>
                        <SelectItem value="anxiety">Anxiety</SelectItem>
                        <SelectItem value="agitation">Agitation/Delirium</SelectItem>
                        <SelectItem value="secretions">Secretions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Severity</Label>
                    <Select value={symptomForm.severity} onValueChange={(v) => setSymptomForm({...symptomForm, severity: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mild">Mild</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                        <SelectItem value="severe">Severe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ineffective"
                      checked={symptomForm.currentTreatmentIneffective}
                      onChange={(e) => setSymptomForm({...symptomForm, currentTreatmentIneffective: e.target.checked})}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="ineffective">Current treatment ineffective</Label>
                  </div>
                  
                  <div>
                    <Label>Additional Context</Label>
                    <Textarea
                      value={symptomForm.additionalContext}
                      onChange={(e) => setSymptomForm({...symptomForm, additionalContext: e.target.value})}
                      placeholder="Patient has been on current regimen for 2 weeks..."
                      rows={3}
                    />
                  </div>
                  
                  <Button 
                    onClick={getRecommendation}
                    disabled={loading}
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    data-testid="get-recommendation-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Get AI Recommendation
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* AI Recommendation Result */}
            {recommendation && (
              <Card className="border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-violet-600" />
                      AI Recommendation
                    </CardTitle>
                    <Badge className="bg-violet-100 text-violet-700">
                      Confidence: {Math.round((recommendation.confidence_score || 0.8) * 100)}%
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Recommended Medication */}
                  {recommendation.recommended_medication && (
                    <div className="p-4 bg-white rounded-lg border border-violet-200">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <Pill className="w-5 h-5 text-violet-600" />
                        Recommended Medication
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Medication</p>
                          <p className="font-medium">{recommendation.recommended_medication.name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Strength</p>
                          <p className="font-medium">{recommendation.recommended_medication.strength}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Form</p>
                          <p className="font-medium">{recommendation.recommended_medication.form}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Schedule</p>
                          <Badge variant="outline">{recommendation.recommended_medication.schedule}</Badge>
                        </div>
                      </div>
                      <div className="mt-3 p-2 bg-slate-50 rounded text-sm">
                        <strong>Sig:</strong> {recommendation.recommended_medication.sig}
                      </div>
                      <Button 
                        onClick={() => sendToPharmacy(recommendation.recommended_medication)}
                        className="mt-4 bg-green-600 hover:bg-green-700"
                        disabled={loading}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send to Pharmacy
                      </Button>
                    </div>
                  )}

                  {/* Clinical Rationale */}
                  {recommendation.clinical_rationale && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">Clinical Rationale</h4>
                      <p className="text-blue-700 text-sm">{recommendation.clinical_rationale}</p>
                    </div>
                  )}

                  {/* Interactions */}
                  {recommendation.interactions && recommendation.interactions.length > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Drug Interactions
                      </h4>
                      <div className="space-y-2">
                        {recommendation.interactions.map((int, idx) => (
                          <div key={idx} className={`p-2 rounded ${severityColors[int.severity]}`}>
                            <strong>{int.drug}</strong>: {int.effect}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Monitoring */}
                  {recommendation.monitoring && recommendation.monitoring.length > 0 && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Monitoring Recommendations
                      </h4>
                      <ul className="list-disc list-inside text-sm text-green-700">
                        {recommendation.monitoring.map((m, idx) => (
                          <li key={idx}>{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Caregiver Instructions */}
                  {recommendation.caregiver_instructions && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <h4 className="font-medium text-slate-800 mb-2">Caregiver Instructions</h4>
                      <p className="text-slate-600 text-sm">{recommendation.caregiver_instructions}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Voice Prescribe Tab */}
          <TabsContent value="voice" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5 text-blue-600" />
                  Voice-to-Prescription
                </CardTitle>
                <CardDescription>
                  Speak your prescription and AI will parse it into structured format
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Patient ID *</Label>
                    <Input
                      value={patientProfile.patientId}
                      onChange={(e) => setPatientProfile({...patientProfile, patientId: e.target.value})}
                      placeholder="P12345"
                    />
                  </div>
                  <div>
                    <Label>Patient Name *</Label>
                    <Input
                      value={patientProfile.patientName}
                      onChange={(e) => setPatientProfile({...patientProfile, patientName: e.target.value})}
                      placeholder="John Smith"
                    />
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-center">
                  <Mic className="w-12 h-12 mx-auto mb-4 text-blue-500" />
                  <p className="text-sm text-blue-700 mb-4">
                    Example: "Morphine sulfate 15 milligrams tablet, quantity 30, take one tablet every 4 hours as needed for pain, no refills"
                  </p>
                  <VoiceInput
                    onTranscription={handleVoiceTranscription}
                    context="prescription"
                    enhanced={true}
                    outputFormat="structured"
                    patientId={patientProfile.patientId}
                    patientName={patientProfile.patientName}
                    placeholder="Click to dictate prescription"
                    buttonVariant="default"
                    className="justify-center"
                  />
                </div>

                {voicePrescription && (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2">Transcription</h4>
                      <p className="text-sm text-slate-600">{voicePrescription.transcription}</p>
                    </div>

                    {voicePrescription.parsed_prescription && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Parsed Prescription
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <p className="text-slate-500">Medication</p>
                            <p className="font-medium">{voicePrescription.parsed_prescription.medication}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Strength</p>
                            <p className="font-medium">{voicePrescription.parsed_prescription.strength}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Form</p>
                            <p className="font-medium">{voicePrescription.parsed_prescription.form}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Quantity</p>
                            <p className="font-medium">{voicePrescription.parsed_prescription.quantity}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Days Supply</p>
                            <p className="font-medium">{voicePrescription.parsed_prescription.days_supply}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Refills</p>
                            <p className="font-medium">{voicePrescription.parsed_prescription.refills}</p>
                          </div>
                        </div>
                        <div className="mt-3 p-2 bg-white rounded">
                          <strong>Sig:</strong> {voicePrescription.parsed_prescription.sig}
                        </div>
                        
                        {voicePrescription.ready_to_send ? (
                          <Button 
                            onClick={() => sendToPharmacy(voicePrescription.parsed_prescription)}
                            className="mt-4 bg-green-600 hover:bg-green-700"
                            disabled={loading}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Send to Pharmacy
                          </Button>
                        ) : (
                          <div className="mt-4 flex items-center gap-2 text-amber-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">Please review before sending</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comfort Kits Tab */}
          <TabsContent value="comfort-kit" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-teal-600" />
                    Standard Comfort Kit
                  </CardTitle>
                  <CardDescription>Emergency symptom management medications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { med: "Morphine Sulfate", strength: "20mg/mL", qty: "30mL", route: "oral/sublingual" },
                      { med: "Lorazepam", strength: "2mg/mL", qty: "30mL", route: "oral/sublingual" },
                      { med: "Haloperidol", strength: "5mg/mL", qty: "30mL", route: "oral/sublingual" },
                      { med: "Glycopyrrolate", strength: "0.2mg/mL", qty: "30mL", route: "oral" },
                      { med: "Ondansetron ODT", strength: "4mg", qty: "10 tablets", route: "sublingual" },
                      { med: "Acetaminophen Supp", strength: "650mg", qty: "12", route: "rectal" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{item.med}</p>
                          <p className="text-xs text-slate-500">{item.strength} • {item.qty}</p>
                        </div>
                        <Badge variant="outline">{item.route}</Badge>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    <Send className="w-4 h-4 mr-2" />
                    Order Standard Kit
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-600" />
                    Extended Comfort Kit
                  </CardTitle>
                  <CardDescription>Additional crisis medications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { med: "Midazolam", strength: "5mg/mL", qty: "10mL", route: "injectable" },
                      { med: "Hydromorphone", strength: "2mg/mL", qty: "30mL", route: "injectable" },
                      { med: "Scopolamine Patch", strength: "1.5mg", qty: "4 patches", route: "transdermal" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div>
                          <p className="font-medium text-sm">{item.med}</p>
                          <p className="text-xs text-slate-500">{item.strength} • {item.qty}</p>
                        </div>
                        <Badge variant="outline">{item.route}</Badge>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    <Send className="w-4 h-4 mr-2" />
                    Order Extended Kit
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
